import { MouseEventHandler, useEffect, useMemo, useRef } from 'react'
import parser, { DOMNode, Element } from 'html-react-parser'
import styled from 'styled-components'
import { match } from 'ts-pattern'
import { RouletteContent } from '@/components/domains/events/roulette-content'
import { RouletteContent6Divided } from '@/components/domains/events/roulette-content-6divided'
import { ENV_CONFIG } from '@/constants/env/config'
import ROUTES, { checkAuthRoute } from '@/constants/legacy/constRoutes'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import useEventDetailEventTracker from '@/containers/event-tracker/event-detail.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { hasSignedIn } from '@/containers/hooks/useUserInfo'
import { Bridge } from '@/utils/bridge/bridge'
import appBridgeProvider, { appBridgeLogin, isInAppFlag } from '@/utils/utilBridge'
import { openExternalUrl, openExternalWithBridgeUrl } from '@/utils/utilURL'

const CUSTOM_HOST = 'http://localhost:4000'

const EventDetailDescription = () => {
  const { ad } = EventContainer.useContainer()
  const { trackClickEventsaleDetailImage } = useEventDetailEventTracker()
  const { push } = useCustomRouter()
  const bridgeLogin = appBridgeLogin()

  const containerRef = useRef<HTMLElement>(null)

  const sendTrackingEvent = (adUrl: string) => {
    trackClickEventsaleDetailImage({
      adId: ad.id,
      adSlug: ad.slug ?? '',
      adName: ad.name,
      adUrl,
    })
  }

  /**
   * 리뷰 리스트 페이지로 이동하는 경우 앱/웹에서 로그인 처리를 필요
   * 이미지 링크가 mypage가 아닌 경우에는 로그인 없이 이미지의 링크로 이동
   * @TODO: EventDetailContentLegacy.tsx의 코드를 옮겨온 것으로 불필요한 로직으로 확인되는 부분은 삭제처리 필요
   */
  const handleClick: MouseEventHandler<HTMLElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()

    let el: HTMLElement | null = e.target as HTMLElement

    let actionEl: HTMLElement | null = el
    while (actionEl && actionEl !== e.currentTarget) {
      const onClickCode = actionEl.getAttribute('data-onclick')
      if (onClickCode) {
        try {
          // eslint-disable-next-line no-new-func
          const executeInlineScript = new Function(onClickCode)
          executeInlineScript()
        } catch (error) {}
        return
      }
      actionEl = actionEl.parentNode as HTMLElement
    }

    while (el && el !== e.currentTarget && el.tagName !== 'A') {
      el = el.parentNode as HTMLElement
    }
    if (el && el.tagName !== 'A') {
      return
    }

    const targetLink = el?.getAttribute('href')

    if (!targetLink) {
      return
    }

    if (targetLink.includes('#')) {
      try {
        const targetUrl = new URL(targetLink, window.location.origin)
        const currentUrl = new URL(window.location.href)

        const isSamePage = targetUrl.origin === currentUrl.origin && targetUrl.pathname === currentUrl.pathname

        if (isSamePage && targetUrl.hash) {
          const targetId = targetUrl.hash.replace('#', '')
          const targetElement = document.getElementById(targetId)

          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
            return
          }
        }
      } catch (error) {
        console.error('URL 파싱 에러:', error)
      }
    }

    // 앰플리튜드 이벤트 트래킹
    sendTrackingEvent(targetLink)

    // kakao app link 처리
    const webProtocols = ['http:', 'https:']
    const isAppLink = !webProtocols.includes(new URL(targetLink).protocol)
    if (isAppLink && isInAppFlag) {
      openExternalWithBridgeUrl(targetLink)
      return
    }

    // BLOG: 'https://fitpetmall.com/blog',
    // BLOG_WWW: 'https://www.fitpetmall.com/blog',
    // blog 이동 url 처리
    if (targetLink.startsWith(ROUTES.BLOG) || targetLink.startsWith(ROUTES.BLOG_WWW)) {
      return window.open(targetLink)
    }

    if (!targetLink.startsWith(ENV_CONFIG.fitpetmallHost) && !targetLink.startsWith(CUSTOM_HOST)) {
      return openExternalUrl(targetLink)
    }
    const getPathname = (url: string) => {
      if (url.startsWith(ENV_CONFIG.fitpetmallHost) || url.startsWith(CUSTOM_HOST)) {
        return url.replace(ENV_CONFIG.fitpetmallHost, '').replace(CUSTOM_HOST, '').replace('/mall', '')
      }
      return ''
    }

    const deepLinkList: {
      [key: string]: { value: [string, string]; isAuthRoute: boolean }
    } = {
      '/': { value: ['fitpetmall', '/main'], isAuthRoute: false },
      '/mypage': { value: ['mypage', '/main'], isAuthRoute: true },
      '/mypage/like-list': { value: ['fitpetmall', '/detail/mypage/like-list'], isAuthRoute: false },
      '/mypage/recently-list': { value: ['fitpetmall', '/detail/mypage/recently-list'], isAuthRoute: true },
      '/home/events': { value: ['fitpetmall', '/home/events'], isAuthRoute: false },
    }

    let returnUrl = ''
    try {
      returnUrl = new URL(targetLink, window.location.origin).searchParams.get('return_url') || getPathname(targetLink)
    } catch (error) {
      returnUrl = getPathname(targetLink)
    }
    const deepLink = deepLinkList[returnUrl]
    const isDeepLink = Object.keys(deepLinkList).includes(returnUrl) // 네이티브 화면 여부 ts-mattern에서 사용하기 위해 정의
    const isAuthRoute = checkAuthRoute(targetLink) // 로그인 필요한 페이지 여부
    const isLogin = hasSignedIn() // 로그인 여부

    /*
    @example
      만약 isInAppFlag이 true이면:
      - 사용자가 로그인하고 딥 링크 플래그가 true인 경우:
        - appBridgeProvider를 사용하여 브릿지를 얻어와서 deepLinkData.deeplinkValue를 사용하여 goDeepLink를 실행합니다.
      - 사용자가 로그인하고 딥 링크 플래그가 false인 경우:
      ...
*/
    if (isInAppFlag) {
      match({ isLogin, isAuthRoute, isDeepLink })
        .with({ isLogin: true, isDeepLink: true }, () =>
          appBridgeProvider((bridge: Bridge) => {
            return bridge.goDeepLink(...deepLink.value)
          })
        )
        .with({ isLogin: true, isDeepLink: false }, () => push(targetLink))
        .with({ isLogin: false, isDeepLink: true, isAuthRoute: true }, () =>
          appBridgeProvider((bridge: Bridge) =>
            bridge.login().then(() => {
              bridgeLogin(window.getToken())
              return bridge.goDeepLink(...deepLink.value)
            })
          )
        )
        .with({ isLogin: false, isDeepLink: true, isAuthRoute: false }, () =>
          appBridgeProvider((bridge: Bridge) => bridge.goDeepLink(...deepLink.value))
        )
        .with({ isLogin: false, isDeepLink: false, isAuthRoute: true }, () =>
          appBridgeProvider((bridge: Bridge) =>
            bridge.login().then(() => {
              bridgeLogin(window.getToken())
              return push(targetLink)
            })
          )
        )
        .with({ isLogin: false, isDeepLink: false, isAuthRoute: false }, () => push(targetLink))
        .exhaustive()
    } else {
      match({ isLogin, isAuthRoute })
        .with({ isLogin: true }, () => push(targetLink))
        .with({ isLogin: false, isAuthRoute: true }, () =>
          push({
            pathname: ROUTES.ACCOUNTS.LOGIN,
            query: {
              [QueryStringKeyEnum.ReturnUrl]: returnUrl,
            },
          })
        )
        .with({ isLogin: false, isAuthRoute: false }, () => push(targetLink))
        .exhaustive()
    }
  }

  const options = {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element) {
        // div#roulette-section 처리
        if (domNode.name === 'div' && domNode.attribs && domNode.attribs.id === 'roulette-section') {
          return <RouletteContent slug={ad.slug ?? ''} />
        }
        // 🌟 새로 추가: 6등분 룰렛 섹션 처리
        if (domNode.name === 'div' && domNode.attribs && domNode.attribs.id === 'roulette-section-6divided') {
          return <RouletteContent6Divided slug={ad.slug ?? ''} />
        }
        // img 처리
        if (domNode.name === 'img') {
          return <img src={domNode.attribs.src} alt={domNode.attribs.alt} style={{ width: '100%', display: 'block' }} />
        }
      }
    },
  }

  const cleanDescription = useMemo(() => {
    if (!ad.description) return ''

    return ad.description
      .replaceAll('&nbsp;', ' ') // non-breaking space를 일반 공백으로 변환
      .replace(/\s+/g, ' ') // 연속된 공백을 하나로 줄임
      .replace(/\s+on([a-z]+)=(['"])(.*?)\2/gi, (match: string, eventName: string, quote: string, code: string) => {
        return ` data-on-${eventName.toLowerCase()}=${quote}${code}${quote}`
      })
      .trim() // 앞뒤 공백 제거
  }, [ad.description])

  useEffect(() => {
    if (!containerRef.current || !cleanDescription) return

    // 1. 함수 선언부(<script>) 강제 실행
    const scripts = Array.from<HTMLScriptElement>(containerRef.current.querySelectorAll('script'))
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script')
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value)
      })
      if (oldScript.innerHTML) {
        newScript.appendChild(document.createTextNode(oldScript.innerHTML))
      }
      oldScript.parentNode?.replaceChild(newScript, oldScript)
    })

    // 2. data-on-* 네이티브 리스너 부착
    const allElements = Array.from(containerRef.current.querySelectorAll('*'))
    allElements.forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-on-')) {
          const eventType = attr.name.replace('data-on-', '')
          const scriptCode = attr.value
          el.addEventListener(eventType, function (event) {
            try {
              // eslint-disable-next-line no-new-func
              const executeEvent = new Function('event', scriptCode)
              executeEvent.call(el, event)
            } catch (error) {}
          })
        }
      })
    })

    // 🌟 3. 궁극의 스크롤 로직: MutationObserver 적용
    if (window.location.hash) {
      // 상용 환경에서 해시 뒤에 붙을 수 있는 쓰레기값(?) 및 공백 완벽 제거
      const rawHash = window.location.hash.replace('#', '')
      const targetId = decodeURIComponent(rawHash).split('?')[0].trim()

      const executeScroll = (targetEl: HTMLElement) => {
        // 목표 위치로 스크롤 즉시 이동 (UX 저하 없음)
        targetEl.scrollIntoView({ behavior: 'auto', block: 'start' })

        // 🌟 찾자마자 주소창에서 해시 즉시 제거
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
        }

        // Next.js 라우터 간섭 방어용: 티 안 나게 0.15초 간격으로 2번만 더 제자리에 고정해 줍니다.
        let count = 0
        const interval = setInterval(() => {
          targetEl.scrollIntoView({ behavior: 'auto', block: 'start' })
          count++
          if (count >= 2) clearInterval(interval)
        }, 150)
      }

      const targetElement = document.getElementById(targetId)

      if (targetElement) {
        // [케이스 A] 이미 DOM에 그려져 있다면 딜레이 없이 즉시 실행!
        executeScroll(targetElement)
      } else {
        // 🌟 [케이스 B] 아직 렌더링 전이라면 브라우저에게 감시를 지시합니다.
        const observer = new MutationObserver((mutations, obs) => {
          const el = document.getElementById(targetId)
          if (el) {
            // 브라우저가 요소를 포착한 그 즉시(0ms 딜레이) 스크롤 실행!
            executeScroll(el)
            obs.disconnect() // 찾았으니 감시를 종료하여 성능을 확보합니다.
          }
        })

        // containerRef 내부의 자식 요소들이 그려지는 과정을 감시
        observer.observe(containerRef.current, { childList: true, subtree: true })

        // 안전장치: 네트워크 이슈 등으로 요소가 영원히 안 생길 경우 2초 뒤 깔끔하게 감시 종료
        setTimeout(() => {
          observer.disconnect()
        }, 2000)
      }
    }
  }, [cleanDescription, ad?.slug])

  if (!ad?.description) return null

  return (
    <StyledContainer ref={containerRef} onClick={handleClick}>
      {cleanDescription && parser(cleanDescription, options)}
    </StyledContainer>
  )
}

const StyledContainer = styled.section`
  p {
    margin-bottom: 0; /* global.less.css에서 기본값으로 1em을 주고 있어서 0으로 처리*/
  }

  /** a tag 자식으로 img가 있는 경우, a tag가 이미지영역 클릭시에 onClick이 되어야하므로 block으로 처리 */
  a {
    display: block;
  }
`

export default EventDetailDescription
